import { html, render } from "lit-html";
import { classMap } from "lit-html/directives/class-map.js";
import { repeat } from "lit-html/directives/repeat.js";
import { build } from "@escapio/shared/state/CalendarGenerator";
import { stateful } from "@escapio/shared/components/element-builder";
import { MAX_NUM_NIGHTS } from "../../state/travelData";
import {
	getWeekDays,
	isBefore,
	isSameDay,
	isAfter,
	addDays,
	firstDayOfWeek,
	monthTranslations,
	weekDayTranslations,
} from "@escapio/shared/l10n/date";

const today = new Date();
const NUM_MONTHS = 13;

class Calendar extends HTMLElement {
	static getInitialState() {
		return {
			hoveredDate: null,
			selectionStart: null,
			selectionEnd: null,
		};
	}

	scrollSelectionIntoView() {
		this.getMonthNodeByDate(this.selectionStart).scrollIntoView({
			behavior: "smooth",
			block: "nearest",
		});
	}

	getMonthNodeByDate(date) {
		date = date || new Date();
		return this.querySelector(
			`.month-${date.getFullYear()}-${date.getMonth()}`,
		);
	}

	render() {
		const months = build(today, NUM_MONTHS, firstDayOfWeek);

		const template = html`
			${repeat(
				months,
				({ month, year, weeks }, index) => html`
					<div
						class="calendar-container month-${year}-${month}"
						data-index=${index}
					>
						<header class="calendar__month__header">
							${monthTranslations[month]} ${year}
						</header>
						<table class="calendar__month__head">
							<thead>
								<tr>
									${repeat(
										getWeekDays(firstDayOfWeek),
										weekday => html` <th>${weekDayTranslations[weekday]}</th> `,
									)}
								</tr>
							</thead>
						</table>
						<table class="calendar__month">
							<tbody class="calendar__weeks">
								${repeat(
									weeks,
									weekdays => html`
										<tr>
											${repeat(weekdays, day => this.renderDay(day))}
										</tr>
									`,
								)}
							</tbody>
						</table>
					</div>
				`,
			)}
		`;
		render(template, this, { host: this });
	}

	renderDay({ date, isPast, isToday, isCurrentMonth, isFutureBeyond }) {
		const selectable =
			!this.selectionEnd &&
			this.selectionStart &&
			isAfter(date, this.selectionStart) &&
			!isAfter(date, addDays(this.selectionStart, MAX_NUM_NIGHTS));

		const classes = {
			calendar__day: true,
			"calendar__day--is-past": isPast,
			"calendar__day--is-today": isToday,
			"calendar__day--current-month": isCurrentMonth,
			"calendar__day--selection-start": isSameDay(date, this.selectionStart),
			"calendar__day--selection-range":
				isAfter(date, this.selectionStart) && isBefore(date, this.selectionEnd),
			"calendar__day--selection-end": isSameDay(date, this.selectionEnd),
			"calendar__day--selection-complete":
				(this.selectionStart && this.selectionEnd) ||
				(!this.selectionStart && !this.selectionEnd),
			"calendar__day--selectable": selectable,
			"calendar__day--selection-hover":
				selectable && this.hoveredDate && !isAfter(date, this.hoveredDate),
		};

		return html` <td
			class="${classMap(classes)}"
			@click=${() => this.onDayClick(date, isPast, isFutureBeyond)}
			@mouseover=${() => this.onMouseOver(date, isPast)}
		>
			${date.getDate()}
		</td>`;
	}

	onDayClick(date, isPast, isFutureBeyond) {
		if (isPast || isFutureBeyond) {
			return;
		}
		if (
			!this.selectionStart ||
			isBefore(date, this.selectionStart) ||
			this.selectionEnd
		) {
			this.dispatchEvent(
				new CustomEvent("startDateSelected", { detail: { date } }),
			);
			return;
		}
		if (isSameDay(date, this.selectionStart)) {
			return;
		}
		if (isAfter(date, addDays(this.selectionStart, MAX_NUM_NIGHTS))) {
			this.dispatchEvent(new CustomEvent("invalidDateRangeSelected"));
			return;
		}
		this.dispatchEvent(
			new CustomEvent("endDateSelected", { detail: { date } }),
		);
	}

	onMouseOver(date, isPast) {
		if (!isPast) {
			this.hoveredDate = date;
		}
	}
}

customElements.define("esc-calendar", stateful(Calendar));
